import { useCallback, useEffect, useRef, useState } from "react";
import { type Issue, listIssues, createIssue, startIssue } from "../api/issues";
import { copyConversation } from "../api/conversations";
import { useMe } from "../hooks/useMe";
import { DAEMON_URL } from "../config";

type Props = {
  project: string;
  /**
   * Open the copied conversation as a focused new column. Wired down from App
   * so the "Continue in my chat" takeover surfaces the clone additively instead
   * of reloading the page.
   */
  onOpenConversation?: (conversationId: number, branch: string | null, worktreeDir?: string | null) => void;
};

const STATUS_BADGE: Record<Issue["status"], string> = {
  open: "issue-badge issue-badge-open",
  working: "issue-badge issue-badge-working",
  paused: "issue-badge issue-badge-paused",
  done: "issue-badge issue-badge-done",
  error: "issue-badge issue-badge-error",
};

function fmtUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Admin-only budget meter. Spend/budget are admin-gated at the call site
 * (rendered only when `isAdmin`), so this never leaks cost to members.
 */
function BudgetMeter({ spentCents, budgetCents }: { spentCents: number; budgetCents: number }) {
  const pct = budgetCents > 0 ? Math.min(100, Math.round((spentCents / budgetCents) * 100)) : 0;
  const cls = pct >= 100 ? "" : pct >= 85 ? "warn" : "ok";
  return (
    <span className="issue-meter" title={`${pct}% of budget`}>
      <span className="issue-meter-track">
        <span className={`issue-meter-fill ${cls}`} style={{ width: `${pct}%` }} />
      </span>
      <span className="issue-cost">
        {fmtUsd(spentCents)} / {fmtUsd(budgetCents)}
      </span>
    </span>
  );
}

function fmtAgo(ts: number): string {
  const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

type IssueEvent = {
  kind: string;
  at: number;
  text?: string;
  tool?: string;
  label?: string;
  branch?: string;
  status?: string;
  note?: string | null;
  spentCents?: number;
  budgetCents?: number;
  signedOff?: boolean;
};

function fmtEvent(ev: IssueEvent, isAdmin: boolean): string {
  switch (ev.kind) {
    case "spawn": return `▶ spawned on ${ev.branch}`;
    case "tool_use": return `▸ ${ev.label ?? ev.tool ?? "tool"}`;
    case "tool_ok": return `  ✓ ok`;
    case "tool_error": return `  ✗ error`;
    case "thinking": return `… thinking`;
    case "text": {
      const t = (ev.text ?? "").trim();
      if (t.length === 0) return "";
      return t.length > 200 ? "💬 " + t.slice(0, 197) + "…" : "💬 " + t;
    }
    case "budget_warn": return isAdmin
      ? `⚠ 85% budget reached (${fmtUsd(ev.spentCents ?? 0)} / ${fmtUsd(ev.budgetCents ?? 0)})`
      : `⚠ wrapping up`;
    case "budget_kill": return isAdmin
      ? `■ budget exhausted, killing (${fmtUsd(ev.spentCents ?? 0)})`
      : `■ run limit reached, stopping`;
    case "wall_timeout": return `■ wall-clock timeout, killing`;
    case "push": return `↑ pushed ${ev.branch}`;
    case "reviewer_start": return isAdmin
      ? `🔍 reviewer started (budget ${fmtUsd(ev.budgetCents ?? 0)})`
      : `🔍 reviewer started`;
    case "reviewer_pushed": return `↑ reviewer pushed fixes`;
    case "reviewer_done": return ev.signedOff ? `🔍 reviewer signed off` : `🔍 reviewer finished`;
    case "status": return `· status → ${ev.status}${ev.note ? ` — ${ev.note}` : ""}`;
    case "end": return `■ run ended`;
    default: return `· ${ev.kind}`;
  }
}

// How close to the bottom (px) still counts as "pinned" — a couple of log
// lines of slack so the tail keeps following despite sub-pixel rounding.
const LOG_STICK_THRESHOLD_PX = 32;

/**
 * Whether a scroll container is close enough to its bottom to keep following
 * the tail. Pure (works on any {scrollHeight, scrollTop, clientHeight}) so the
 * stick-to-bottom boundary can be unit-tested without a real layout — happy-dom
 * reports all three as 0, so the live-DOM test cannot exercise this math.
 */
export function isPinnedToBottom(
  m: { scrollHeight: number; scrollTop: number; clientHeight: number },
  threshold = LOG_STICK_THRESHOLD_PX,
): boolean {
  return m.scrollHeight - m.scrollTop - m.clientHeight <= threshold;
}

function IssueLogStream({
  project,
  issueId,
  status,
  isAdmin,
}: {
  project: string;
  issueId: number;
  status: Issue["status"];
  isAdmin: boolean;
}) {
  const [events, setEvents] = useState<IssueEvent[]>([]);
  // Whether this run has emitted its terminal "end" event. Drives the
  // empty-state copy: before end we are still "waiting for events…"; only after
  // end (or for a long-finished run whose buffer was dropped) is it truthful to
  // say "no events recorded".
  const [ended, setEnded] = useState(false);
  const logRef = useRef<HTMLDivElement | null>(null);
  // Whether the viewport is pinned to the bottom of THIS log. Starts pinned so
  // a freshly-expanded log follows the tail; flips off the moment the user
  // scrolls up to read earlier events, and back on when they return to bottom.
  const pinnedRef = useRef(true);

  useEffect(() => {
    const url = `${DAEMON_URL}/projects/${encodeURIComponent(project)}/issues/${issueId}/stream`;
    const es = new EventSource(url, { withCredentials: true });
    const handler = (e: MessageEvent) => {
      try {
        const ev = JSON.parse(e.data) as IssueEvent;
        setEvents((prev) => [...prev.slice(-199), ev]);
        if (ev.kind === "end") setEnded(true);
      } catch { /* ignore */ }
    };
    es.addEventListener("event", handler);
    return () => {
      es.removeEventListener("event", handler);
      es.close();
    };
  }, [project, issueId]);

  // Follow the tail by scrolling THIS log's own container only. Never use
  // scrollIntoView: it scrolls every scrollable ancestor (the outer
  // issues-panel) too, so when multiple issues stream events at once the whole
  // panel jumps up and down and buries the create form at the top. We also only
  // follow when the user is pinned to the bottom of this specific log, so
  // scrolling up to read history is never yanked back down.
  useEffect(() => {
    const el = logRef.current;
    if (!el || !pinnedRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [events]);

  const onScroll = () => {
    const el = logRef.current;
    if (!el) return;
    pinnedRef.current = isPinnedToBottom(el);
  };

  return (
    <div className="issue-log" ref={logRef} onScroll={onScroll}>
      {events.length === 0 && (
        <div className="issue-log-empty">
          {!ended && (status === "working" || status === "open")
            ? "waiting for events…"
            : "no events recorded"}
        </div>
      )}
      {events.map((ev, idx) => {
        const line = fmtEvent(ev, isAdmin);
        if (!line) return null;
        return (
          <div key={`${ev.at}-${idx}`} className={`issue-log-line issue-log-${ev.kind}`}>
            {line}
          </div>
        );
      })}
    </div>
  );
}

export default function IssuesPanel({ project, onOpenConversation }: Props) {
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  const [issues, setIssues] = useState<Issue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [budgetUsd, setBudgetUsd] = useState("10");
  // Admin-only: create + push the branch without spawning the AI (zero API).
  const [dryRun, setDryRun] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const refresh = useCallback(async () => {
    try {
      const list = await listIssues(project);
      setIssues(list);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [project]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Light polling for the LIST itself — per-issue live updates flow through
  // the SSE stream subscription in IssueLogStream.
  useEffect(() => {
    if (!issues) return;
    const anyActive = issues.some((i) => i.status === "working");
    if (!anyActive) return;
    const t = setInterval(() => void refresh(), 5000);
    return () => clearInterval(t);
  }, [issues, refresh]);

  // Just after starting a run the status is often still "open" (the worktree is
  // being set up), so the "anyActive" poll above never starts and the row never
  // flips to working → done without a manual reload. Fire a short burst of
  // refreshes on start so the badge tracks the run. Best-effort, self-clearing.
  const pollBurst = useCallback(() => {
    for (const ms of [800, 2000, 4000, 7000, 11000, 16000]) {
      setTimeout(() => void refresh(), ms);
    }
  }, [refresh]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creating) return; // idempotency: ignore double-submit while in flight
    if (!title.trim()) return;
    setCreating(true);
    setError(null);
    try {
      // Only admins are allowed to override the per-issue budget — backend
      // ignores the field for members, but skip sending it from the client
      // too for clarity.
      const usd = Number(budgetUsd);
      const issue = await createIssue(project, {
        title: title.trim(),
        body: body.trim(),
        ...(isAdmin ? { budgetUsd: Number.isFinite(usd) && usd > 0 ? usd : 10 } : {}),
        // Dry run is an admin-only testing toggle; ignore any stray member value.
        ...(isAdmin && dryRun ? { dryRun: true } : {}),
        autoStart: true,
      });
      setTitle("");
      setBody("");
      setBudgetUsd("10");
      setDryRun(false);
      // Auto-expand the new row so the user immediately sees the live log.
      setExpanded((prev) => new Set([...prev, issue.id]));
      await refresh();
      pollBurst();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const onStart = async (id: number) => {
    setError(null);
    try {
      await startIssue(project, id);
      setExpanded((prev) => new Set([...prev, id]));
      await refresh();
      pollBurst();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onTakeOver = async (i: Issue) => {
    if (i.conversation_id == null) return;
    setError(null);
    try {
      const { conversation } = await copyConversation(project, i.conversation_id);
      // Open the freshly-copied conversation as a focused new column so the
      // user can keep talking to the bot where the issue run left off.
      onOpenConversation?.(conversation.id, conversation.branch ?? null, conversation.worktree_dir ?? null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  return (
    <div className="issues-panel">
      {error && (
        <div className="error-banner" role="alert" aria-live="polite">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      <form className="issue-create" onSubmit={(e) => void submit(e)} aria-label="Create issue">
        <label className="issue-field-label">
          Title
          <input
            type="text"
            placeholder="What needs to be fixed?"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={creating}
            aria-label="Issue title"
          />
        </label>
        <label className="issue-field-label">
          Details
          <textarea
            placeholder="Context, file paths, error messages, acceptance criteria. The AI works on a fresh branch from this description."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={creating}
            rows={4}
            aria-label="Issue details"
          />
        </label>
        <div className="issue-create-row">
          {isAdmin ? (
            <label>
              Budget $
              <input
                type="text"
                inputMode="decimal"
                value={budgetUsd}
                onChange={(e) => setBudgetUsd(e.target.value)}
                disabled={creating || dryRun}
                className="issue-budget"
                aria-label="Budget in USD"
                title={dryRun ? "Budget is irrelevant for a dry run (no AI runs)" : undefined}
              />
            </label>
          ) : (
            <span className="issue-budget-locked">Budget set by admin</span>
          )}
          {isAdmin && (
            <label
              className="issue-dryrun"
              title="Create and push the branch peter/issue-<id> without running the AI — no API cost. For testing the pipeline."
            >
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                disabled={creating}
                aria-label="Dry run: create the branch, skip the AI"
              />
              Dry run (no AI)
            </label>
          )}
          <button type="submit" disabled={creating || !title.trim()}>
            {creating ? "Creating…" : dryRun ? "Create branch (dry run)" : "Create & start"}
          </button>
        </div>
      </form>

      {issues === null && <p className="placeholder">Loading…</p>}
      {issues !== null && issues.length === 0 && (
        <p className="placeholder">No issues yet. Create one above and the AI will pick it up.</p>
      )}
      {issues !== null && issues.length > 0 && (
        <ul className="issue-list">
          {issues.map((i) => {
            const isOpen = expanded.has(i.id);
            const canTakeOver = i.conversation_id != null && (i.status === "done" || i.status === "paused" || i.status === "error");
            return (
              <li key={i.id} className="issue-row">
                <div className="issue-row-head">
                  <button
                    type="button"
                    className="issue-disclosure"
                    onClick={() => toggle(i.id)}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? "Collapse issue log" : "Expand issue log"}
                  >
                    {isOpen ? "▾" : "▸"}
                  </button>
                  <span className={STATUS_BADGE[i.status]}>{i.status}</span>
                  {i.dry_run ? (
                    <span className="issue-badge issue-badge-dry" title="Dry run — branch only, no AI was run">
                      dry run
                    </span>
                  ) : null}
                  <span className="issue-title">#{i.id} · {i.title}</span>
                  {isAdmin && (
                    <BudgetMeter
                      spentCents={i.spent_cents ?? 0}
                      budgetCents={i.budget_cents ?? 0}
                    />
                  )}
                  <span className="issue-age">{fmtAgo(i.updated_at)}</span>
                </div>
                {i.source === "notion" && i.source_url && (
                  <div className="issue-meta">
                    <span>source </span>
                    <a href={i.source_url} target="_blank" rel="noopener noreferrer">
                      Notion · #{i.id} ↗
                    </a>
                  </div>
                )}
                {i.branch && (
                  <div className="issue-meta">
                    <span>branch </span>
                    <code>{i.branch}</code>
                  </div>
                )}
                {i.status_note && <div className="issue-note">{i.status_note}</div>}
                <div className="issue-actions">
                  {i.status === "open" && (
                    <button type="button" className="issue-start" onClick={() => void onStart(i.id)}>
                      Start
                    </button>
                  )}
                  {canTakeOver && (
                    <button type="button" className="issue-takeover" onClick={() => void onTakeOver(i)}>
                      Continue in my chat
                    </button>
                  )}
                </div>
                {isOpen && (
                  <IssueLogStream
                    project={project}
                    issueId={i.id}
                    status={i.status}
                    isAdmin={isAdmin}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
